import React, { type ReactNode } from 'react';

import cx from 'classnames';
import { Icon } from 'design-system-web';

import { useSectionCommands } from '../../commands/CustomizationCommandsContext';
import { useLabels } from '../../i18n/useLabels';
import type { SectionViewModel } from '../../viewmodels/createSectionViewModel';
import { Badge } from '../badges/Badge';
import { InlineRetry } from '../questions/InlineRetry';
import { RowSpinner } from '../questions/RowSpinner';
import { SectionActionsMenu } from './SectionActionsMenu';
import { sectionNameId, sectionTableId } from './sectionDomIds';

import styles from './SectionHeader.scss';

type Props = {
  viewModel: SectionViewModel;
  dragHandle?: ReactNode;
};

export function SectionHeader({ viewModel, dragHandle }: Props) {
  const labels = useLabels();
  const { selectEntry, retry, toggle } = useSectionCommands();

  const { id, spinner, failure, menuEntries } = viewModel;
  const hasMenu = !spinner && menuEntries.length > 0;

  const handleToggle = () => {
    toggle(id);
  };

  return (
    <div className={cx(styles.header, viewModel.isExpanded && styles.expanded)}>
      <div className={styles.titleCell}>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={viewModel.isExpanded}
          aria-controls={sectionTableId(id)}
          aria-labelledby={sectionNameId(id)}
          data-test="section-toggle"
          onClick={handleToggle}
        >
          <Icon
            name={viewModel.isExpanded ? 'chevron-down' : 'chevron-right'}
          />
        </button>
        {dragHandle}
        {viewModel.badges.map(badge => (
          <Badge key={badge.kind} viewModel={badge} />
        ))}
        <div className={styles.titleStack}>
          <span
            id={sectionNameId(id)}
            className={cx(styles.name, viewModel.isHidden && styles.hidden)}
          >
            {viewModel.name}
          </span>
          {viewModel.counts && (
            <span className={styles.counts}>
              {labels.formatMessage(viewModel.counts)}
            </span>
          )}
        </div>
      </div>
      <div className={styles.responseCell}>
        {viewModel.isExpanded && (
          <span className={styles.responseLabel} aria-hidden="true">
            {labels.responseTypeHeading}
          </span>
        )}
        <div className={styles.actions}>
          {failure && (
            <InlineRetry viewModel={failure} onRetry={() => retry(id)} />
          )}
          {spinner && <RowSpinner viewModel={spinner} />}
          {hasMenu && (
            <SectionActionsMenu
              viewModel={viewModel}
              onSelect={entryId => selectEntry(id, entryId)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
